import { call, select, takeEvery } from 'redux-saga/effects';
import { GlobalActions, GlobalPut } from '@ps20-frontend/shop-integration-redux';
import { App, Vorvertrag } from '@ps20-frontend/frontend-models';
import dayjs from 'dayjs';
import { Umstellungsstatus } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { IAppState } from '../appState';
import { angebotIsTarifiert } from '../selectors/warenkorb.selector';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { selectUmstellungsstatusForVorvertrag } from '../selectors/vorvertragsUebersicht.selector';

export function* hauptfaelligkeitProviderSaga() {
  yield takeEvery(
    GlobalActions.INITIAL_HAUPTFAELLIGKEIT_REQUESTED,
    safeSaga(provideHauptfaelligkeit, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    GlobalActions.HAUPTFAELLIGKEIT_CHANGED,
    safeSaga(provideHauptfaelligkeitJHP, App.SHOP_DASHBOARD),
  );
}

export function* provideHauptfaelligkeit() {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();

  let initialHauptfaelligkeit = dayjs().add(1, 'd').format(DATEFORMAT_SHORT_ISO);

  const tarifierteAngeboteMitVorvertraege = state.warenkorb
    .filter(produkt => produkt.appId !== App.JHP && angebotIsTarifiert(produkt))
    .filter(produkt => produkt.vorvertragsNummern?.length > 0);

  if (tarifierteAngeboteMitVorvertraege) {
    const ablaufAusVorvertraege = ausVorvertraegeVorbelegen(
      state,
      tarifierteAngeboteMitVorvertraege,
    );
    initialHauptfaelligkeit = ablaufAusVorvertraege || initialHauptfaelligkeit;
  }
  yield call(globalPut.put, new GlobalActions().hauptfaelligkeitChanged(initialHauptfaelligkeit));
}

export function* provideHauptfaelligkeitJHP() {
  const globalPut = GlobalPut.getInstance();

  const year_offset = dayjs().month() < 3 ? 0 : 1; // before april, then no offset
  const initialHauptfaelligkeit = dayjs()
    .date(1)
    .month(3)
    .add(year_offset, 'year')
    .format(DATEFORMAT_SHORT_ISO);

  yield call(
    globalPut.put,
    new GlobalActions().hauptfaelligkeitJHPChanged(initialHauptfaelligkeit),
  );
}

function ausVorvertraegeVorbelegen(
  state: IAppState,
  tarifierteAngebote: WarenkorbAngebot[],
): string {
  const tarifierteVorvertraege = state.vorvertraege.filter(
    vv =>
      vv.ablaufdatum &&
      tarifierteAngebote
        .map(produkt => produkt?.vorvertragsNummern)
        .flat()
        .some(
          nummer =>
            nummer.vertragsnummer === vv.vertragsnummer && nummer.spLaufnummer === vv.spLaufnummer,
        ),
  );

  const beibehalteneTarfierteVorvertrag: Vorvertrag = tarifierteVorvertraege.find(
    vertrag =>
      selectUmstellungsstatusForVorvertrag(vertrag, state) === Umstellungsstatus.BEIBEHALTEN,
  );

  if (beibehalteneTarfierteVorvertrag) {
    return dayjs(beibehalteneTarfierteVorvertrag.ablaufdatum).format(DATEFORMAT_SHORT_ISO);
  }

  const isAblaufSameForAllProducts =
    tarifierteVorvertraege?.length > 0 &&
    tarifierteVorvertraege.map(vv => vv.ablaufdatum).every((val, i, arr) => val === arr[0]);

  return isAblaufSameForAllProducts
    ? dayjs(tarifierteVorvertraege[0].ablaufdatum).format(DATEFORMAT_SHORT_ISO)
    : null;
}
